import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState, useMemo } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line,
  RadialBarChart, RadialBar, ComposedChart,
} from "recharts";
import {
  Eye, MousePointerClick, Target, TrendingUp, TrendingDown, Share2,
  Users, BarChart3, Activity, Clock, Globe, Smartphone, Monitor, Tablet,
  ArrowUpRight, ArrowDownRight, Minus, Loader2, Download, RefreshCw,
  ChevronDown, Zap, FileText, Mail, Filter, Sparkles, Layers,
  Hash, Play, ArrowRight, Radio,
} from "lucide-react";

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const COLORS = ["#6366F1", "#8B5CF6", "#EC4899", "#10B981", "#F59E0B", "#3B82F6", "#14B8A6", "#F97316", "#EF4444", "#84CC16"];
const PLATFORM_COLORS: Record<string, string> = {
  facebook: "#1877F2", instagram: "#E4405F", tiktok: "#000000", x: "#1DA1F2",
  linkedin: "#0A66C2", google: "#4285F4", youtube: "#FF0000", pinterest: "#E60023",
};
const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok", x: "X (Twitter)",
  linkedin: "LinkedIn", google: "Google", youtube: "YouTube", pinterest: "Pinterest",
};

const KPI_ICONS: Record<string, any> = {
  impressions: Eye, clicks: MousePointerClick, conversions: Target,
  engagement: Activity, reach: Users, shares: Share2,
  ctr: TrendingUp, convRate: Target, engRate: Activity,
};

const PERIOD_OPTIONS = [
  { value: 7, label: "7D" },
  { value: 14, label: "14D" },
  { value: 30, label: "30D" },
  { value: 90, label: "90D" },
  { value: 365, label: "1Y" },
];

type TabId = "overview" | "platforms" | "campaigns" | "content" | "realtime";

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function Analytics() {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [timelineTypes, setTimelineTypes] = useState(["impression", "click", "conversion"]);
  const [platformFilter, setPlatformFilter] = useState("");

  // Queries
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["analytics-overview", days],
    queryFn: () => api.get<any>(`/analytics/overview?days=${days}`),
  });

  const { data: timeline, isLoading: timelineLoading } = useQuery({
    queryKey: ["analytics-timeline", days, timelineTypes, platformFilter],
    queryFn: () => {
      const params = new URLSearchParams({ days: String(days), types: timelineTypes.join(",") });
      if (platformFilter) params.set("platform", platformFilter);
      return api.get<any>(`/analytics/timeline?${params}`);
    },
  });

  const { data: platformData } = useQuery({
    queryKey: ["analytics-platforms", days],
    queryFn: () => api.get<any>(`/analytics/platforms?days=${days}`),
    enabled: activeTab === "overview" || activeTab === "platforms",
  });

  const { data: campaignData } = useQuery({
    queryKey: ["analytics-campaigns", days],
    queryFn: () => api.get<any[]>(`/analytics/campaigns?days=${days}`),
    enabled: activeTab === "overview" || activeTab === "campaigns",
  });

  const { data: contentData } = useQuery({
    queryKey: ["analytics-content", days],
    queryFn: () => api.get<any[]>(`/analytics/content?days=${days}`),
    enabled: activeTab === "content",
  });

  const { data: sourcesData } = useQuery({
    queryKey: ["analytics-sources", days],
    queryFn: () => api.get<any>(`/analytics/sources?days=${days}`),
    enabled: activeTab === "overview",
  });

  const { data: realtimeData, refetch: refetchRealtime } = useQuery({
    queryKey: ["analytics-realtime"],
    queryFn: () => api.get<any>("/analytics/realtime?limit=50"),
    enabled: activeTab === "realtime",
    refetchInterval: activeTab === "realtime" ? 10000 : false,
  });



  const kpis = overview?.kpis || [];
  const hasData = kpis.some((k: any) => k.value > 0);

  const tabs: { id: TabId; label: string; icon: any }[] = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "platforms", label: "Platforms", icon: Globe },
    { id: "campaigns", label: "Campaigns", icon: Target },
    { id: "content", label: "Content", icon: FileText },
    { id: "realtime", label: "Real-Time", icon: Radio },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Cross-platform performance intelligence</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex bg-gray-100 rounded-xl p-0.5">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => setDays(p.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  days === p.value ? "bg-white shadow-sm text-indigo-700" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>


        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 border border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-white shadow-sm text-indigo-700 border border-gray-200"
                : "text-gray-500 hover:text-gray-700 border border-transparent"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>



      {/* Content */}
      {overviewLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : activeTab === "overview" ? (
        <OverviewTab
          overview={overview}
          kpis={kpis}
          hasData={hasData}
          timeline={timeline}
          timelineLoading={timelineLoading}
          timelineTypes={timelineTypes}
          setTimelineTypes={setTimelineTypes}
          platformData={platformData}
          platformFilter={platformFilter}
          setPlatformFilter={setPlatformFilter}
          campaignData={campaignData}
          sourcesData={sourcesData}
          days={days}
        />
      ) : activeTab === "platforms" ? (
        <PlatformsTab platformData={platformData} />
      ) : activeTab === "campaigns" ? (
        <CampaignsTab campaignData={campaignData} />
      ) : activeTab === "content" ? (
        <ContentTab contentData={contentData} />
      ) : activeTab === "realtime" ? (
        <RealtimeTab data={realtimeData} onRefresh={refetchRealtime} />
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════
function OverviewTab({
  overview, kpis, hasData, timeline, timelineLoading,
  timelineTypes, setTimelineTypes, platformData, platformFilter,
  setPlatformFilter, campaignData, sourcesData, days,
}: any) {
  const toggleType = (type: string) => {
    setTimelineTypes((prev: string[]) =>
      prev.includes(type) ? prev.filter((t: string) => t !== type) : [...prev, type]
    );
  };

  const typeButtons = [
    { id: "impression", label: "Impressions", color: "#6366F1" },
    { id: "click", label: "Clicks", color: "#EC4899" },
    { id: "conversion", label: "Conversions", color: "#10B981" },
    { id: "engagement", label: "Engagement", color: "#F59E0B" },
    { id: "reach", label: "Reach", color: "#3B82F6" },
    { id: "share", label: "Shares", color: "#8B5CF6" },
  ];

  if (!hasData) {
    return (
      <div className="card p-16 text-center">
        <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <BarChart3 className="w-8 h-8 text-indigo-500" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No analytics data yet</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
          Analytics data will appear here as your campaigns, content, and social posts generate impressions, clicks, and conversions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.slice(0, 6).map((kpi: any) => {
          const Icon = KPI_ICONS[kpi.key] || BarChart3;
          const isPositive = kpi.change > 0;
          const isNeutral = kpi.change === 0;
          return (
            <div key={kpi.key} className="card p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-indigo-600" />
                </div>
                {!isNeutral && (
                  <span className={`flex items-center gap-0.5 text-xs font-semibold ${isPositive ? "text-green-600" : "text-red-500"}`}>
                    {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {Math.abs(kpi.change)}%
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {kpi.format === "percent" ? `${kpi.value}%` : kpi.value.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Events", value: overview?.totalEvents?.toLocaleString() || "0", icon: Hash, color: "bg-purple-50 text-purple-600" },
          { label: "Active Campaigns", value: overview?.activeCampaigns || 0, icon: Target, color: "bg-pink-50 text-pink-600" },
          { label: "Published Content", value: overview?.publishedContent || 0, icon: FileText, color: "bg-blue-50 text-blue-600" },
          { label: "Connected Platforms", value: overview?.connectedPlatforms || 0, icon: Globe, color: "bg-green-50 text-green-600" },
        ].map((s) => (
          <div key={s.label} className="card p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Rate KPIs (CTR, Conv Rate, Eng Rate) */}
      {kpis.length > 6 && (
        <div className="grid grid-cols-3 gap-4">
          {kpis.slice(6).map((kpi: any) => {
            const Icon = KPI_ICONS[kpi.key] || BarChart3;
            const pct = Math.min(kpi.value, 100);
            return (
              <div key={kpi.key} className="card p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-700">{kpi.label}</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{kpi.value}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-700"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Timeline Chart */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Performance Timeline</h3>
            <p className="text-xs text-gray-400 mt-0.5">Daily metrics over the selected period</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {typeButtons.map((tb) => (
              <button
                key={tb.id}
                onClick={() => toggleType(tb.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  timelineTypes.includes(tb.id)
                    ? "border-transparent text-white shadow-sm"
                    : "border-gray-200 text-gray-400 hover:border-gray-300"
                }`}
                style={timelineTypes.includes(tb.id) ? { backgroundColor: tb.color } : {}}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </div>

        {timelineLoading ? (
          <div className="h-[320px] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
        ) : timeline?.data?.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={timeline.data}>
              <defs>
                {typeButtons.map((tb) => (
                  <linearGradient key={tb.id} id={`grad-${tb.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={tb.color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={tb.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v: string) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                labelFormatter={(label: string) => new Date(label).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              />
              {typeButtons.filter((tb) => timelineTypes.includes(tb.id)).map((tb) => (
                <Area key={tb.id} type="monotone" dataKey={tb.id} stroke={tb.color} fill={`url(#grad-${tb.id})`} strokeWidth={2} dot={false} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[320px] flex items-center justify-center text-sm text-gray-400">No timeline data available</div>
        )}
      </div>

      {/* Bottom Grid: Platform Pie + Campaign Bars + Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platform Pie */}
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Platform Share</h3>
          {platformData?.platforms?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={platformData.platforms.map((p: any) => ({ name: PLATFORM_LABELS[p.name] || p.name, value: p.total }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    strokeWidth={2}
                  >
                    {platformData.platforms.map((p: any, i: number) => (
                      <Cell key={i} fill={PLATFORM_COLORS[p.name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {platformData.platforms.slice(0, 5).map((p: any, i: number) => (
                  <div key={p.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[p.name] || COLORS[i] }} />
                      <span className="text-gray-700">{PLATFORM_LABELS[p.name] || p.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.total.toLocaleString()}</span>
                      <span className={`text-xs ${p.change > 0 ? "text-green-600" : p.change < 0 ? "text-red-500" : "text-gray-400"}`}>
                        {p.change > 0 ? "+" : ""}{p.change}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">No platform data</div>
          )}
        </div>

        {/* Top Campaigns */}
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Top Campaigns</h3>
          {campaignData?.length > 0 ? (
            <div className="space-y-3">
              {campaignData.slice(0, 6).map((cb: any, i: number) => {
                const impressions = cb.metrics?.impression || 0;
                const clicks = cb.metrics?.click || 0;
                const maxImp = campaignData[0]?.metrics?.impression || 1;
                return (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-800 truncate max-w-[60%]">{cb.campaign?.name || "Unknown"}</span>
                      <span className="text-gray-500">{impressions.toLocaleString()}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-pink-500 transition-all" style={{ width: `${(impressions / maxImp) * 100}%` }} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>{clicks.toLocaleString()} clicks</span>
                      <span>{cb.ctr}% CTR</span>
                      <span className={`badge ${cb.campaign?.status === "active" ? "badge-green" : "badge-gray"}`}>{cb.campaign?.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">No campaign data</div>
          )}
        </div>

        {/* Traffic Sources */}
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Traffic Sources</h3>
          {sourcesData?.sources?.length > 0 ? (
            <div className="space-y-6">
              {/* Sources breakdown */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-400 uppercase">Source</p>
                {sourcesData.sources.slice(0, 5).map((s: any, i: number) => (
                  <div key={s.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                      <span className="capitalize text-gray-700">{s.name}</span>
                    </div>
                    <span className="font-medium tabular-nums">{s.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              {/* Devices */}
              {sourcesData.devices?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-400 uppercase">Devices</p>
                  <div className="flex gap-3">
                    {sourcesData.devices.slice(0, 3).map((d: any) => {
                      const DevIcon = d.name === "mobile" ? Smartphone : d.name === "tablet" ? Tablet : Monitor;
                      return (
                        <div key={d.name} className="flex-1 p-2 bg-gray-50 rounded-lg text-center">
                          <DevIcon className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs font-semibold text-gray-700">{d.value.toLocaleString()}</p>
                          <p className="text-[10px] text-gray-400 capitalize">{d.name}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Countries */}
              {sourcesData.countries?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-400 uppercase">Top Countries</p>
                  <div className="flex flex-wrap gap-2">
                    {sourcesData.countries.slice(0, 6).map((c: any) => (
                      <span key={c.name} className="px-2 py-1 bg-gray-50 rounded text-xs font-medium text-gray-600">
                        {c.name} <span className="text-gray-400">{c.value.toLocaleString()}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">No source data</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// PLATFORMS TAB
// ═══════════════════════════════════════════════════════
function PlatformsTab({ platformData }: { platformData: any }) {
  if (!platformData?.platforms?.length) {
    return (
      <div className="card p-16 text-center">
        <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-1">No platform data</h3>
        <p className="text-sm text-gray-400">Connect platforms and generate events to see breakdowns here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {platformData.platforms.map((p: any, i: number) => {
          const color = PLATFORM_COLORS[p.name] || COLORS[i % COLORS.length];
          const metrics = p.metrics || {};
          return (
            <div key={p.name} className="card p-5 hover:shadow-md transition-shadow border-l-4" style={{ borderLeftColor: color }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Globe className="w-5 h-5" style={{ color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{PLATFORM_LABELS[p.name] || p.name}</h3>
                    {p.accountName && <p className="text-xs text-gray-400">@{p.accountName}</p>}
                  </div>
                </div>
                <span className={`flex items-center gap-1 text-sm font-semibold ${p.change > 0 ? "text-green-600" : p.change < 0 ? "text-red-500" : "text-gray-400"}`}>
                  {p.change > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : p.change < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                  {Math.abs(p.change)}%
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Impressions", value: metrics.impression || 0 },
                  { label: "Clicks", value: metrics.click || 0 },
                  { label: "CTR", value: `${p.ctr}%` },
                  { label: "Engagement", value: metrics.engagement || 0 },
                  { label: "Conversions", value: metrics.conversion || 0 },
                  { label: "Shares", value: metrics.share || 0 },
                ].map((m) => (
                  <div key={m.label} className="text-center">
                    <p className="text-lg font-bold text-gray-900">{typeof m.value === "number" ? formatNum(m.value) : m.value}</p>
                    <p className="text-[10px] text-gray-400">{m.label}</p>
                  </div>
                ))}
              </div>

              {p.followers > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400">
                  <Users className="w-3 h-3" />
                  <span>{p.followers.toLocaleString()} followers</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Platform Comparison Bar Chart */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Platform Comparison</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={platformData.platforms.map((p: any) => ({
            name: PLATFORM_LABELS[p.name] || p.name,
            impressions: p.metrics?.impression || 0,
            clicks: p.metrics?.click || 0,
            conversions: p.metrics?.conversion || 0,
            engagement: p.metrics?.engagement || 0,
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
            <Legend />
            <Bar dataKey="impressions" fill="#6366F1" radius={[4, 4, 0, 0]} />
            <Bar dataKey="clicks" fill="#EC4899" radius={[4, 4, 0, 0]} />
            <Bar dataKey="conversions" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="engagement" fill="#F59E0B" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CAMPAIGNS TAB
// ═══════════════════════════════════════════════════════
function CampaignsTab({ campaignData }: { campaignData: any[] | undefined }) {
  const [sortKey, setSortKey] = useState("impressions");

  if (!campaignData?.length) {
    return (
      <div className="card p-16 text-center">
        <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-1">No campaign data</h3>
        <p className="text-sm text-gray-400">Create campaigns and track events to see performance here.</p>
      </div>
    );
  }

  const sorted = [...campaignData].sort((a, b) => {
    if (sortKey === "clicks") return (b.metrics?.click || 0) - (a.metrics?.click || 0);
    if (sortKey === "conversions") return (b.metrics?.conversion || 0) - (a.metrics?.conversion || 0);
    if (sortKey === "ctr") return (b.ctr || 0) - (a.ctr || 0);
    return (b.metrics?.impression || 0) - (a.metrics?.impression || 0);
  });

  return (
    <div className="space-y-6">
      {/* Sort Buttons */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Sort by:</span>
        {["impressions", "clicks", "conversions", "ctr"].map((key) => (
          <button
            key={key}
            onClick={() => setSortKey(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              sortKey === key ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "text-gray-500 border-gray-200 hover:border-gray-300"
            }`}
          >
            {key === "ctr" ? "CTR" : key.charAt(0).toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      {/* Campaign Table */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Campaign</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Impressions</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Clicks</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">CTR</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Conversions</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Conv Rate</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{row.campaign?.name || "Unknown"}</p>
                      {row.campaign?.type && <p className="text-xs text-gray-400">{row.campaign.type}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <span className={`badge ${row.campaign?.status === "active" ? "badge-green" : row.campaign?.status === "completed" ? "badge-blue" : "badge-gray"}`}>
                    {row.campaign?.status || "—"}
                  </span>
                </td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.impression || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.click || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{row.ctr}%</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.conversion || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{row.convRate}%</td>
                <td className="px-4 py-4 text-right text-sm font-bold tabular-nums">{row.total.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Campaigns Bar Chart */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Campaign Funnel</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={sorted.slice(0, 8).map((r) => ({
            name: (r.campaign?.name || "Unknown").slice(0, 15),
            impressions: r.metrics?.impression || 0,
            clicks: r.metrics?.click || 0,
            conversions: r.metrics?.conversion || 0,
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
            <Legend />
            <Bar dataKey="impressions" fill="#6366F1" radius={[4, 4, 0, 0]} name="Impressions" />
            <Bar dataKey="clicks" fill="#EC4899" radius={[4, 4, 0, 0]} name="Clicks" />
            <Bar dataKey="conversions" fill="#10B981" radius={[4, 4, 0, 0]} name="Conversions" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CONTENT TAB
// ═══════════════════════════════════════════════════════
function ContentTab({ contentData }: { contentData: any[] | undefined }) {
  if (!contentData?.length) {
    return (
      <div className="card p-16 text-center">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-1">No content performance data</h3>
        <p className="text-sm text-gray-400">Publish content and track events to see what performs best.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Content</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Type</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Impressions</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Clicks</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Engagement</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Shares</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contentData.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-purple-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[250px]">{row.content?.title || "Untitled"}</p>
                      {row.content?.publishedAt && <p className="text-xs text-gray-400">{new Date(row.content.publishedAt).toLocaleDateString()}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-right"><span className="badge badge-gray">{row.content?.type || "—"}</span></td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.impression || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.click || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.engagement || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{(row.metrics?.share || 0).toLocaleString()}</td>
                <td className="px-4 py-4 text-right text-sm font-bold tabular-nums">{row.total.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Content Performance Chart */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Content Performance Ranking</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={contentData.slice(0, 8).map((r) => ({
            name: (r.content?.title || "Untitled").slice(0, 20),
            total: r.total,
          }))} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
            <Bar dataKey="total" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// REAL-TIME TAB
// ═══════════════════════════════════════════════════════
function RealtimeTab({ data, onRefresh }: { data: any; onRefresh: () => void }) {
  if (!data) {
    return (
      <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
    );
  }

  const EVENT_BADGES: Record<string, string> = {
    impression: "bg-indigo-50 text-indigo-700",
    click: "bg-pink-50 text-pink-700",
    conversion: "bg-green-50 text-green-700",
    engagement: "bg-yellow-50 text-yellow-700",
    reach: "bg-blue-50 text-blue-700",
    share: "bg-purple-50 text-purple-700",
  };

  return (
    <div className="space-y-6">
      {/* Live header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm font-semibold text-gray-700">Live Activity Feed</span>
          </div>
          <span className="text-xs text-gray-400">Auto-refreshes every 10s</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600">{data.totalLastHour} events <span className="text-gray-400">last hour</span></span>
          <button onClick={onRefresh} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Sparkline */}
      {data.sparkline?.length > 0 && (
        <div className="card p-4">
          <p className="text-xs text-gray-400 mb-2">Events per minute (last hour)</p>
          <ResponsiveContainer width="100%" height={80}>
            <AreaChart data={data.sparkline}>
              <Area type="monotone" dataKey="value" stroke="#6366F1" fill="#6366F1" fillOpacity={0.1} strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Event List */}
      <div className="card overflow-hidden">
        <div className="divide-y divide-gray-100">
          {data.events?.map((event: any, i: number) => {
            const badgeClass = EVENT_BADGES[event.eventType] || "bg-gray-50 text-gray-700";
            return (
              <div key={event.id || i} className="px-5 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badgeClass} flex-shrink-0`}>
                  {event.eventType}
                </span>
                <span className="text-sm font-medium text-gray-800 tabular-nums w-12 text-right flex-shrink-0">+{event.value}</span>
                <div className="flex-1 min-w-0 flex items-center gap-2 text-xs text-gray-400 overflow-hidden">
                  {event.platform && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{event.platform}</span>}
                  {event.campaign?.name && <span className="truncate">{event.campaign.name}</span>}
                  {event.content?.title && <span className="truncate">• {event.content.title}</span>}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
                  {new Date(event.recordedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              </div>
            );
          })}
        </div>

        {(!data.events || data.events.length === 0) && (
          <div className="py-12 text-center">
            <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No recent events</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// UTILITY
// ═══════════════════════════════════════════════════════
function formatNum(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}
